// "Allow self check-in as a backup" — per office (or company default).
//
// ⚠️ ANTI BUDDY-PUNCHING: off unless an admin explicitly turns it on; only
// meaningful for scanner offices (mode = biometric), so it is disabled for
// anything else. Turning it on is audited (who / since when) and a backup left
// on for more than 3 days is called out here and on the Workforce board.
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CaptureMode, CapturePolicyRow } from '@/services/attendanceService';

export const BACKUP_REMIND_AFTER_DAYS = 3;

const fmtDay = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const BackupCheckinToggle: React.FC<{
  mode: CaptureMode;
  row?: CapturePolicyRow;
  canWrite: boolean;
  busy?: boolean;
  /** The value being saved right now. Shown instead of the stored one so an
   *  untick does not snap back to ticked for the length of the save — which
   *  read as "it didn't take" and invited the click that turned it back ON
   *  (DEF-0mvooe9wg002-5538bd). */
  pending?: boolean;
  onChange: (on: boolean) => void;
}> = ({ mode, row, canWrite, busy, pending, onChange }) => {
  const { t } = useTranslation('admin');
  const scanner = mode === 'biometric';
  const stored = scanner && !!row?.self_checkin_backup;
  const on = scanner && (pending ?? stored);
  const days = row?.self_checkin_backup_days ?? null;
  return (
    <div className="w-full space-y-1">
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={on}
          disabled={!scanner || !canWrite || busy}
          // The intent is the opposite of what the box SHOWS — never the
          // DOM's own idea of `checked`, which React has already reset.
          onChange={() => onChange(!on)}
        />
        {t('att.backup_toggle', { defaultValue: 'Allow self check-in as a backup' })}
      </label>
      {scanner && !canWrite && (
        // A greyed-out box with no reason reads as "my untick didn't work"
        // (HO-0mvq38s7e001-d470ae): say why it can't be changed here.
        <p className="text-xs text-warning" data-testid="backup-no-permission">
          {t('att.backup_no_permission', { defaultValue: "You can't change this setting: it needs permission to edit attendance policies. Ask an HR admin." })}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {scanner
          ? t('att.backup_help', { defaultValue: 'If the scanner is down, employees can check in from the app. Every backup check-in is flagged for HR. Off unless you turn it on.' })
          : t('att.backup_only_scanner', { defaultValue: 'Only for offices that use the scanner.' })}
      </p>
      {on && pending === undefined && row?.self_checkin_backup_since && (
        <p className="text-xs text-muted-foreground">
          {t('att.backup_since', {
            defaultValue: 'Backup check-in on since {{date}} by {{name}}',
            date: fmtDay(row.self_checkin_backup_since),
            name: row.self_checkin_backup_by || t('att.backup_an_admin', { defaultValue: 'an admin' }),
          })}
        </p>
      )}
      {on && pending === undefined && days !== null && days > BACKUP_REMIND_AFTER_DAYS && (
        <p className="text-xs text-warning">
          {t('att.backup_long', { defaultValue: 'Backup check-in has been on for {{n}} days', n: days })}
        </p>
      )}
    </div>
  );
};
